import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ChevronLeft, Send, Flame } from "lucide-react";
import { toast } from "sonner";
import PhoneFrame from "../components/PhoneFrame.jsx";
import api from "../lib/api.js";
import { useAuth } from "../lib/auth.jsx";

export default function MessageThread() {
  const { friendId } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [friend, setFriend] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const endRef = useRef(null);
  const pollRef = useRef(null);

  const load = async () => {
    try {
      const r = await api.get(`/messages/thread/${friendId}`);
      setFriend(r.data.friend);
      setMessages(r.data.messages);
    } catch (e) {
      toast.error("Cannot open thread");
      nav("/messages");
    }
  };

  useEffect(() => {
    load();
    pollRef.current = setInterval(load, 8000);
    return () => clearInterval(pollRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [friendId]);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const send = async () => {
    if (!text.trim() || sending) return;
    setSending(true);
    const optimistic = {
      id: `tmp-${Date.now()}`,
      from_user_id: user.id,
      to_user_id: friendId,
      text: text.trim(),
      created_at: new Date().toISOString(),
    };
    setMessages((m) => [...m, optimistic]);
    setText("");
    try {
      await api.post("/messages/send", { to_user_id: friendId, text: optimistic.text });
      load();
    } catch (e) {
      toast.error("Send failed");
      setMessages((m) => m.filter((x) => x.id !== optimistic.id));
    } finally {
      setSending(false);
    }
  };

  return (
    <PhoneFrame>
      <div className="flex flex-col h-screen">
        <div className="px-5 pt-6 pb-3 border-b border-[#1a2f40] flex items-center gap-3">
          <button onClick={() => nav("/messages")} className="text-slate-400" data-testid="thread-back"><ChevronLeft size={20} /></button>
          <div className="w-10 h-10 rounded-full bg-[#142534] border border-[#1a2f40] flex items-center justify-center font-bold">
            {friend?.name?.[0]}
          </div>
          <div>
            <div className="ps-heading font-bold" data-testid="thread-friend-name">{friend?.name ?? "…"}</div>
            <div className="text-[10px] text-slate-400 inline-flex items-center gap-1">
              <Flame size={10} color="#F59E0B" /> {friend?.streak ?? 0}d streak
            </div>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-2" data-testid="thread-messages">
          {messages.length === 0 && (
            <div className="text-center text-slate-500 text-sm py-10">No messages yet. Say hi 👋</div>
          )}
          {messages.map((m) => {
            const mine = m.from_user_id === user?.id;
            return (
              <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[78%] px-4 py-2.5 rounded-2xl text-sm leading-relaxed ${
                  mine ? "bg-[#2fbf71] text-[#0a1822]" : "bg-[#142534] border border-[#1a2f40]"
                }`}>
                  {m.text}
                </div>
              </div>
            );
          })}
          <div ref={endRef} />
        </div>

        <div className="p-3 border-t border-[#1a2f40] flex gap-2">
          <input
            data-testid="thread-input"
            value={text} onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && send()}
            placeholder="Write a message…"
            className="flex-1 bg-[#142534] border border-[#1a2f40] rounded-full px-4 py-3 text-sm text-white outline-none focus:border-[#2fbf71]"
          />
          <button
            data-testid="thread-send"
            disabled={sending || !text.trim()}
            onClick={send}
            className="w-12 h-12 rounded-full bg-[#2fbf71] text-[#0a1822] flex items-center justify-center disabled:opacity-50"
          >
            <Send size={18} />
          </button>
        </div>
      </div>
    </PhoneFrame>
  );
}
